import fs from 'node:fs/promises';
import path from 'node:path';

import { getQuarantineDir } from '../lib/paths.ts';
import type { Disposition } from './rules.ts';
import type { Finding } from './scan.ts';

const EXPIRY_DAYS = 14;
const DAY_MS = 24 * 60 * 60 * 1000;

// Finding summary persisted in the spool. Carries the rule + location only —
// never the matched bytes, and never the in-leaf offsets (which could let a
// reader slice the secret out of the co-located held snapshot).
export type QuarantineFindingSummary = {
  ruleId: string;
  disposition: Disposition;
  location: { line: number; jsonPointer: string };
};

export type QuarantineEntry = {
  id: string;
  sourcePath: string;
  harness: string;
  harnessInternalId: string;
  filename: string;
  findings: QuarantineFindingSummary[];
  quarantinedAt: string; // ISO-8601
  expiresAt: string; // ISO-8601
};

function metaPath(id: string): string {
  return path.join(getQuarantineDir(), `${id}.meta.json`);
}
function bytesPath(id: string): string {
  return path.join(getQuarantineDir(), `${id}.jsonl`);
}

export type QuarantineThreadFileArgs = {
  id: string;
  tempPath: string;
  sourcePath: string;
  harness: string;
  harnessInternalId: string;
  filename: string;
  findings: Finding[];
  now: Date;
};

/**
 * Hold a thread snapshot locally. Copies the snapshot bytes into the spool and
 * writes a metadata entry (findings reduced to id/disposition/location). Both
 * files are 0600; the dir is 0700. Never uploads.
 */
export async function quarantineThreadFile(
  args: QuarantineThreadFileArgs,
): Promise<QuarantineEntry> {
  const dir = getQuarantineDir();
  await fs.mkdir(dir, { recursive: true, mode: 0o700 });

  // Anchor expiry to the FIRST time this id was held. Re-holding the same
  // snapshot (a repeated failed `release`, or the daemon re-encountering it)
  // must not keep pushing the 14-day expiry out — it should age out on
  // schedule from the original hold.
  const existing = await readQuarantineEntry(args.id);
  const quarantinedAt = existing?.quarantinedAt ?? args.now.toISOString();
  const expiresAt =
    existing?.expiresAt ?? new Date(args.now.getTime() + EXPIRY_DAYS * DAY_MS).toISOString();

  const entry: QuarantineEntry = {
    id: args.id,
    sourcePath: args.sourcePath,
    harness: args.harness,
    harnessInternalId: args.harnessInternalId,
    filename: args.filename,
    findings: args.findings.map((f) => ({
      ruleId: f.ruleId,
      disposition: f.disposition,
      location: f.location,
    })),
    quarantinedAt,
    expiresAt,
  };

  const bytes = await fs.readFile(args.tempPath);
  await fs.writeFile(bytesPath(args.id), bytes, { mode: 0o600 });
  await fs.writeFile(metaPath(args.id), `${JSON.stringify(entry, null, 2)}\n`, { mode: 0o600 });
  return entry;
}

export async function listQuarantine(): Promise<QuarantineEntry[]> {
  let names: string[];
  try {
    names = await fs.readdir(getQuarantineDir());
  } catch {
    return [];
  }
  const entries: QuarantineEntry[] = [];
  for (const name of names) {
    if (!name.endsWith('.meta.json')) continue;
    try {
      entries.push(JSON.parse(await fs.readFile(path.join(getQuarantineDir(), name), 'utf8')));
    } catch {
      // Skip unreadable/corrupt entries rather than failing the whole listing.
    }
  }
  entries.sort((a, b) =>
    a.quarantinedAt < b.quarantinedAt ? -1 : a.quarantinedAt > b.quarantinedAt ? 1 : 0,
  );
  return entries;
}

export async function readQuarantineEntry(id: string): Promise<QuarantineEntry | null> {
  try {
    return JSON.parse(await fs.readFile(metaPath(id), 'utf8'));
  } catch {
    return null;
  }
}

/** Delete a quarantine entry's bytes + meta. Returns false for an unknown id. */
export async function discardQuarantine(id: string): Promise<boolean> {
  let existed = false;
  for (const target of [metaPath(id), bytesPath(id)]) {
    try {
      await fs.rm(target);
      existed = true;
    } catch {
      // already gone
    }
  }
  return existed;
}

/** Discard entries past their expiry. Returns the discarded ids. Never uploads. */
export async function expireQuarantine(now: Date): Promise<string[]> {
  const discarded: string[] = [];
  for (const entry of await listQuarantine()) {
    const expiry = new Date(entry.expiresAt).getTime();
    // A corrupt/unparseable expiry counts as expired — don't let it linger.
    if (Number.isNaN(expiry) || expiry <= now.getTime()) {
      await discardQuarantine(entry.id);
      discarded.push(entry.id);
    }
  }
  return discarded;
}
